import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import ManagerNav from './managernav';
import Milestones from './Milestones';
import { AuthContext } from '../AuthContext';
import {
  VictoryLine,
  VictoryChart,
  VictoryAxis,
  VictoryTheme,
  VictoryPie,
  VictoryBar,
  VictoryTooltip,
  VictoryVoronoiContainer,
  VictoryLabel,
} from 'victory';
import {
  CurrencyPoundIcon,
  ChartBarIcon,
  CalendarIcon,
  ClockIcon,
  BriefcaseIcon,
  UserGroupIcon,
  BuildingOffice2Icon,
  CubeIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import {
  format,
  parseISO,
  startOfWeek,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  endOfWeek,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  isSameWeek,
  isSameMonth,
  isSameQuarter,
  isSameYear,
} from 'date-fns';

export default function ProjectFinancials() {
  const [projects, setProjects] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('all'); // all, weekly, monthly, quarterly, yearly
  const [milestoneType, setMilestoneType] = useState('Weekly');
  const [milestoneRevenue, setMilestoneRevenue] = useState('');
  const [milestoneSubmitting, setMilestoneSubmitting] = useState(false);
  const [milestoneMessage, setMilestoneMessage] = useState('');
  const navigate = useNavigate();
  const user = useContext(AuthContext);
  const canManageMilestones = ['admin', 'manager'].includes(String(user?.access || user?.Access || user?.role || user?.Role || '').toLowerCase());

  useEffect(() => {
    fetchProjects();
    fetchMilestones();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/getproject', {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();

      if (data.success) {
        // Filter for External projects only and sort by StartDate DESC
        const externalProjects = (Array.isArray(data.data) ? data.data : [])
          .filter(p => p.ProjectType === 'External')
          .sort((a, b) => new Date(b.StartDate) - new Date(a.StartDate));
        setProjects(externalProjects);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setLoading(false);
    }
  };

  const fetchMilestones = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/api/milestones', {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch milestones');
      const data = await response.json();
      setMilestones(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      console.error('Error fetching milestones:', error);
      setMilestones([]);
    }
  };

  const handleCreateMilestone = async (event) => {
    event.preventDefault();
    setMilestoneMessage('');

    const numericRevenue = Number(milestoneRevenue);
    if (!Number.isFinite(numericRevenue) || numericRevenue < 0) {
      setMilestoneMessage('Please enter a valid revenue target.');
      return;
    }

    try {
      setMilestoneSubmitting(true);
      const response = await fetch('https://api.insightos.co.uk/api/milestones', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          type: milestoneType,
          revenue: numericRevenue,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to create milestone');
      }

      setMilestoneMessage('Milestone target created successfully.');
      setMilestoneRevenue('');
      await fetchMilestones();
    } catch (error) {
      setMilestoneMessage(error?.message || 'Failed to create milestone');
    } finally {
      setMilestoneSubmitting(false);
    }
  };

  // Parse materials JSON
  const parseMaterials = (materialsString) => {
    if (!materialsString) return [];
    try {
      const parsed = JSON.parse(materialsString);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const periodOptions = [
    { key: 'weekly', label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
    { key: 'quarterly', label: 'Quarterly' },
    { key: 'yearly', label: 'Annual' },
    { key: 'all', label: 'All Time' },
  ];

  const parseDateSafe = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) return dateValue;
    const parsedIso = parseISO(String(dateValue));
    if (!Number.isNaN(parsedIso.getTime())) return parsedIso;
    const parsedFallback = new Date(dateValue);
    return Number.isNaN(parsedFallback.getTime()) ? null : parsedFallback;
  };

  const now = new Date();

  const getProjectId = (project = {}) => project.id ?? project.Id ?? project.ProjectId ?? '';

  const getProjectsByFilter = (filterKey) => {
    if (filterKey === 'all') return projects;
    const referenceDate = new Date();
    return projects.filter((project) => {
      const projectDate = parseDateSafe(project.StartDate);
      if (!projectDate) return false;
      switch (filterKey) {
        case 'weekly':
          return isSameWeek(projectDate, referenceDate, { weekStartsOn: 1 });
        case 'monthly':
          return isSameMonth(projectDate, referenceDate);
        case 'quarterly':
          return isSameQuarter(projectDate, referenceDate);
        case 'yearly':
          return isSameYear(projectDate, referenceDate);
        default:
          return true;
      }
    });
  };

  const toCurrency = (value) => {
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^0-9.-]/g, '');
      const parsedString = Number(cleaned);
      return Number.isFinite(parsedString) ? parsedString : 0;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatDateSafe = (dateValue) => {
    const parsed = parseDateSafe(dateValue);
    if (!parsed) return '';
    return format(parsed, 'dd/MM/yyyy');
  };

  const getNormalCsvData = (filterKey) => {
    const periodProjects = getProjectsByFilter(filterKey);
    return periodProjects.map((project) => ({
      'Project ID': getProjectId(project),
      'Project Name': project.Name || '',
      'Client Name': project.ClientName || '',
      'Industry': project.Industry || '',
      'Type': project.ProjectType || '',
      'Status': Number(project.Completed) === 1 ? 'Completed' : 'Active',
      'Start Date': formatDateSafe(project.StartDate),
      'End Date': formatDateSafe(project.EndDate),
      'Estimated Hours': project.EstimatedHours || 0,
      'Price': toCurrency(project.Price).toFixed(2),
      'Labor Costs': toCurrency(project.LaborCosts).toFixed(2),
      'Net Profit': toCurrency(project.NetProfit).toFixed(2),
      'Profit Margin %': toCurrency(project.Price) > 0
        ? ((toCurrency(project.NetProfit) / toCurrency(project.Price)) * 100).toFixed(2)
        : '0.00',
      'Author': project.Author || '',
      'Objective': project.Objective || '',
      'Process': project.Process || '',
      'Notes': project.Notes || '',
    }));
  };

  const getSageCsvData = (filterKey) => {
    const periodProjects = getProjectsByFilter(filterKey);
    return periodProjects.map((project) => {
      const netAmount = toCurrency(project.Price);
      const taxAmount = 0;
      const grossAmount = netAmount + taxAmount;
      return {
        'Date': formatDateSafe(project.StartDate),
        'Reference': `PROJ-${getProjectId(project)}`,
        'Account Reference': project.ClientName || project.Name || '',
        'Details': `${project.Name || 'Project'} (${Number(project.Completed) === 1 ? 'Completed' : 'Active'})`,
        'Net Amount': netAmount.toFixed(2),
        'Tax Code': 'T0',
        'Tax Amount': taxAmount.toFixed(2),
        'Gross Amount': grossAmount.toFixed(2),
      };
    });
  };

  const getXeroCsvData = (filterKey) => {
    const periodProjects = getProjectsByFilter(filterKey);
    return periodProjects.map((project) => ({
      'Date': formatDateSafe(project.StartDate),
      'DueDate': formatDateSafe(project.EndDate) || formatDateSafe(project.StartDate),
      'ContactName': project.ClientName || project.Name || '',
      'InvoiceNumber': `PROJ-${getProjectId(project)}`,
      'Reference': project.Name || '',
      'Description': project.Description || 'Project services',
      'Quantity': '1',
      'UnitAmount': toCurrency(project.Price).toFixed(2),
      'LineAmount': toCurrency(project.Price).toFixed(2),
      'AccountCode': '200',
      'TaxType': 'NONE',
      'Status': Number(project.Completed) === 1 ? 'AUTHORISED' : 'DRAFT',
    }));
  };

  // Filter projects based on time filter
  const getFilteredProjects = () => {
    if (timeFilter === 'all') return projects;

    const now = new Date();
    return projects.filter(project => {
      const projectDate = parseDateSafe(project.StartDate);
      if (!projectDate) return false;
      switch (timeFilter) {
        case 'weekly':
          return isSameWeek(projectDate, now, { weekStartsOn: 1 });
        case 'monthly':
          return isSameMonth(projectDate, now);
        case 'quarterly':
          return isSameQuarter(projectDate, now);
        case 'yearly':
          return isSameYear(projectDate, now);
        default:
          return true;
      }
    });
  };

  const filteredProjects = getFilteredProjects();

  const safeRevenue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const sumRevenueInRange = (items, startDate, endDate) => {
    return items.reduce((sum, project) => {
      const raw = project?.StartDate;
      if (!raw) return sum;
      const projectDate = parseISO(raw);
      if (Number.isNaN(projectDate.getTime())) return sum;
      if (projectDate >= startDate && projectDate <= endDate) {
        return sum + safeRevenue(project.Price);
      }
      return sum;
    }, 0);
  };

  const weeklyRevenue = sumRevenueInRange(projects, startOfWeek(now, { weekStartsOn: 1 }), endOfWeek(now, { weekStartsOn: 1 }));
  const monthlyRevenue = sumRevenueInRange(projects, startOfMonth(now), endOfMonth(now));
  const quarterlyRevenue = sumRevenueInRange(projects, startOfQuarter(now), endOfQuarter(now));
  const yearlyRevenue = sumRevenueInRange(projects, startOfYear(now), endOfYear(now));
  const milestoneActualRevenue = {
    Weekly: weeklyRevenue,
    Monthly: monthlyRevenue,
    Quarterly: quarterlyRevenue,
    Yearly: yearlyRevenue,
  };

  const normalizeType = (value = '') => {
    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'weekly' || normalized === 'week') return 'Weekly';
    if (normalized === 'monthly' || normalized === 'month') return 'Monthly';
    if (normalized === 'quarterly' || normalized === 'quartley' || normalized === 'quarter') return 'Quarterly';
    if (normalized === 'yearly' || normalized === 'year') return 'Yearly';
    return null;
  };

  const latestMilestoneByType = milestones.reduce((acc, item) => {
    const type = normalizeType(item?.Type);
    if (!type) return acc;
    const currentCreated = acc[type]?.CreatedAt ? new Date(acc[type].CreatedAt) : new Date(0);
    const nextCreated = item?.CreatedAt ? new Date(item.CreatedAt) : new Date(0);
    if (!acc[type] || nextCreated > currentCreated) {
      acc[type] = item;
    }
    return acc;
  }, {});

  const getTargetByFilter = () => {
    if (timeFilter === 'weekly') return Number(latestMilestoneByType.Weekly?.Revenue || 0);
    if (timeFilter === 'monthly') return Number(latestMilestoneByType.Monthly?.Revenue || 0);
    if (timeFilter === 'quarterly') return Number(latestMilestoneByType.Quarterly?.Revenue || 0);
    if (timeFilter === 'yearly') return Number(latestMilestoneByType.Yearly?.Revenue || 0);
    return 0;
  };

  const activeTarget = getTargetByFilter();

  // Calculate aggregated financials
  const calculateFinancials = () => {
    const totals = filteredProjects.reduce(
      (acc, project) => ({
        totalRevenue: acc.totalRevenue + (project.Price || 0),
        totalNetProfit: acc.totalNetProfit + (project.NetProfit || 0),
        totalLaborCosts: acc.totalLaborCosts + (project.LaborCosts || 0),
        projectCount: acc.projectCount + 1,
      }),
      { totalRevenue: 0, totalNetProfit: 0, totalLaborCosts: 0, projectCount: 0 }
    );

    const avgProfitMargin =
      totals.totalRevenue > 0
        ? ((totals.totalNetProfit / totals.totalRevenue) * 100).toFixed(1)
        : 0;

    return { ...totals, avgProfitMargin };
  };

  const financials = calculateFinancials();

  // Prepare data for line chart (revenue and profit over time)
  const getLineChartData = () => {
    const sortedProjects = [...filteredProjects].sort(
      (a, b) => (parseDateSafe(a.StartDate)?.getTime() || 0) - (parseDateSafe(b.StartDate)?.getTime() || 0)
    );

    const revenueData = sortedProjects
      .map(p => {
        const startDate = parseDateSafe(p.StartDate);
        if (!startDate) return null;
        return {
          x: format(startDate, 'MMM dd'),
          y: toCurrency(p.Price),
          label: `${p.Name}: £${toCurrency(p.Price).toLocaleString()}`,
        };
      })
      .filter(Boolean);

    const profitData = sortedProjects
      .map(p => {
        const startDate = parseDateSafe(p.StartDate);
        if (!startDate) return null;
        return {
          x: format(startDate, 'MMM dd'),
          y: toCurrency(p.NetProfit),
          label: `${p.Name}: £${toCurrency(p.NetProfit).toLocaleString()}`,
        };
      })
      .filter(Boolean);

    return { revenueData, profitData };
  };

  // Prepare data for bar chart (monthly aggregation)
  const getMonthlyData = () => {
    const monthlyAgg = {};
    
    projects.forEach(project => {
      const parsedStartDate = parseDateSafe(project.StartDate);
      if (!parsedStartDate) return;
      const month = format(parsedStartDate, 'MMM yyyy');
      if (!monthlyAgg[month]) {
        monthlyAgg[month] = { revenue: 0, profit: 0, costs: 0 };
      }
      monthlyAgg[month].revenue += toCurrency(project.Price);
      monthlyAgg[month].profit += toCurrency(project.NetProfit);
      monthlyAgg[month].costs += toCurrency(project.LaborCosts);
    });

    return Object.entries(monthlyAgg)
      .map(([month, data]) => ({
        month,
        revenue: data.revenue,
        profit: data.profit,
        costs: data.costs,
        totalRevenue: data.revenue,
      }))
      .slice(-12); // Last 12 months
  };

  const lineChartData = getLineChartData();
  const monthlyData = getMonthlyData();
  const revenueTrendWithTarget = lineChartData.revenueData.map((point) => ({
    ...point,
    target: activeTarget,
  }));

  // Get profit margin color
  const getProfitMarginColor = (margin) => {
    if (margin < 20) return 'text-red-600 bg-red-50';
    if (margin <= 30) return 'text-yellow-600 bg-yellow-50';
    return 'text-emerald-600 bg-emerald-50';
  };

  const getProfitMarginBgColor = (margin) => {
    if (margin < 20) return '#ef4444';
    if (margin <= 30) return '#eab308';
    return '#10b981';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ManagerNav />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Project Financials Dashboard</h1>
          <p className="mt-2 text-sm text-gray-600">
            Track revenue, profit margins, and costs across all external projects
          </p>
        </div>

        {/* Time Filter Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {['all', 'weekly', 'monthly', 'quarterly', 'yearly'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setTimeFilter(filter)}
                  className={`${
                    timeFilter === filter
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  } whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium capitalize`}
                >
                  {filter === 'all' ? 'All Time' : filter}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="mb-8 rounded-lg border border-gray-200 bg-white p-5 shadow">
          <h3 className="text-lg font-semibold text-gray-900">Download CSV Exports</h3>
          <p className="mt-1 text-sm text-gray-600">
            Download Normal, Sage, or Xero-compatible CSV files for weekly, monthly, quarterly, annual, or all-time projects.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3">
            {periodOptions.map((period) => (
              <div key={period.key} className="flex flex-wrap items-center gap-2 rounded-md border border-gray-100 p-3">
                <span className="min-w-[90px] text-sm font-semibold text-gray-800">{period.label}</span>
                <CSVLink
                  data={getNormalCsvData(period.key)}
                  filename={`project_financials_${period.key}_normal.csv`}
                  className="inline-flex items-center gap-x-1.5 rounded-md bg-slate-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-600"
                >
                  <DocumentArrowDownIcon className="h-4 w-4" aria-hidden="true" />
                  Normal CSV
                </CSVLink>
                <CSVLink
                  data={getSageCsvData(period.key)}
                  filename={`project_financials_${period.key}_sage.csv`}
                  className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500"
                >
                  <DocumentArrowDownIcon className="h-4 w-4" aria-hidden="true" />
                  Sage CSV
                </CSVLink>
                <CSVLink
                  data={getXeroCsvData(period.key)}
                  filename={`project_financials_${period.key}_xero.csv`}
                  className="inline-flex items-center gap-x-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-500"
                >
                  <DocumentArrowDownIcon className="h-4 w-4" aria-hidden="true" />
                  Xero CSV
                </CSVLink>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CurrencyPoundIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      £{financials.totalRevenue.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ChartBarIcon className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Net Profit</dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      £{financials.totalNetProfit.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-6 w-6 text-orange-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Labor Costs</dt>
                    <dd className="text-2xl font-bold text-gray-900">
                      £{financials.totalLaborCosts.toLocaleString()}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BriefcaseIcon className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Avg. Margin</dt>
                    <dd
                      className={`text-2xl font-bold ${
                        financials.avgProfitMargin < 20
                          ? 'text-red-600'
                          : financials.avgProfitMargin <= 30
                          ? 'text-yellow-600'
                          : 'text-emerald-600'
                      }`}
                    >
                      {financials.avgProfitMargin}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CalendarIcon className="h-6 w-6 text-violet-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Quarterly Revenue</dt>
                    <dd className="text-2xl font-bold text-gray-900">£{quarterlyRevenue.toLocaleString()}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

        </div>

        <Milestones
          actualRevenueByType={milestoneActualRevenue}
          milestones={milestones}
          formatDateSafe={formatDateSafe}
        />

        {canManageMilestones && (
          <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow">
            <h3 className="text-lg font-semibold text-gray-900">Create Milestone Target</h3>
            <p className="mt-1 text-sm text-gray-600">Set a new target for weekly, monthly, quarterly, or yearly revenue.</p>
            <form onSubmit={handleCreateMilestone} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label htmlFor="milestoneType" className="block text-sm font-medium text-gray-700">Type</label>
                <select
                  id="milestoneType"
                  value={milestoneType}
                  onChange={(e) => setMilestoneType(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                >
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>

              <div>
                <label htmlFor="milestoneRevenue" className="block text-sm font-medium text-gray-700">Revenue Target (£)</label>
                <input
                  id="milestoneRevenue"
                  type="number"
                  min="0"
                  step="0.01"
                  value={milestoneRevenue}
                  onChange={(e) => setMilestoneRevenue(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
                  placeholder="e.g. 25000"
                  required
                />
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={milestoneSubmitting}
                  className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {milestoneSubmitting ? 'Saving...' : 'Save Milestone'}
                </button>
              </div>
            </form>
            {milestoneMessage && (
              <p className="mt-3 text-sm text-indigo-700">{milestoneMessage}</p>
            )}
          </div>
        )}

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue & Profit Trend */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Revenue & Profit Trend
            </h3>
            {lineChartData.revenueData.length > 0 ? (
              <VictoryChart
                theme={VictoryTheme.material}
                height={300}
                containerComponent={<VictoryVoronoiContainer />}
              >
                <VictoryAxis
                  style={{
                    tickLabels: { fontSize: 10, angle: -45, textAnchor: 'end' },
                  }}
                />
                <VictoryAxis
                  dependentAxis
                  tickFormat={(t) => `£${(t / 1000).toFixed(0)}k`}
                  style={{ tickLabels: { fontSize: 10 } }}
                />
                <VictoryLine
                  data={revenueTrendWithTarget}
                  style={{ data: { stroke: '#3b82f6', strokeWidth: 3 } }}
                  labels={({ datum }) => datum.label}
                  labelComponent={<VictoryTooltip />}
                />
                {activeTarget > 0 && (
                  <VictoryLine
                    data={revenueTrendWithTarget}
                    y="target"
                    style={{ data: { stroke: '#f97316', strokeWidth: 2, strokeDasharray: '8,5' } }}
                  />
                )}
                <VictoryLine
                  data={lineChartData.profitData}
                  style={{ data: { stroke: '#10b981', strokeWidth: 3 } }}
                  labels={({ datum }) => datum.label}
                  labelComponent={<VictoryTooltip />}
                />
              </VictoryChart>
            ) : (
              <p className="text-gray-500 text-center py-8">No data available</p>
            )}
            <div className="flex justify-center gap-4 mt-2">
              <div className="flex items-center">
                <div className="w-4 h-0.5 bg-blue-600 mr-2"></div>
                <span className="text-xs text-gray-600">Revenue</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-0.5 bg-emerald-600 mr-2"></div>
                <span className="text-xs text-gray-600">Profit</span>
              </div>
              {activeTarget > 0 && (
                <div className="flex items-center">
                  <div className="w-4 h-0.5 border-t-2 border-dashed border-orange-500 mr-2"></div>
                  <span className="text-xs text-gray-600">Target</span>
                </div>
              )}
            </div>
          </div>

          {/* Monthly Revenue Breakdown */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Monthly Revenue Breakdown (Last 12 Months)
            </h3>
            {monthlyData.length > 0 ? (
              <VictoryChart theme={VictoryTheme.material} height={300} domainPadding={20}>
                <VictoryAxis
                  style={{
                    tickLabels: { fontSize: 9, angle: -45, textAnchor: 'end' },
                  }}
                />
                <VictoryAxis
                  dependentAxis
                  tickFormat={(t) => `£${(t / 1000).toFixed(0)}k`}
                  style={{ tickLabels: { fontSize: 10 } }}
                />
                <VictoryBar
                  data={monthlyData.map(d => ({ x: d.month, y: d.totalRevenue }))}
                  style={{ data: { fill: '#3b82f6' } }}
                />
              </VictoryChart>
            ) : (
              <p className="text-gray-500 text-center py-8">No data available</p>
            )}
          </div>
        </div>

        {/* Individual Project Cards */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-5 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Project Details ({filteredProjects.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-200">
            {filteredProjects.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-500">No external projects found for the selected period</p>
              </div>
            ) : (
              filteredProjects.map((project) => {
                const materials = parseMaterials(project.Materials);
                const profitMargin =
                  project.Price > 0
                    ? ((project.NetProfit / project.Price) * 100).toFixed(1)
                    : 0;
                const usedMaterials = materials.filter(m => m.used === true);
                const totalMaterialsCost = usedMaterials.reduce(
                  (sum, m) => sum + (m.unit_cost || 0) * (m.usedquantity || 0),
                  0
                );

                return (
                  <div key={getProjectId(project)} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="text-xl font-bold text-gray-900">{project.Name}</h4>
                          {project.Completed === 1 ? (
                            <span className="inline-flex items-center gap-x-1.5 rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-700">
                              <CheckCircleIcon className="h-4 w-4" />
                              Completed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-x-1.5 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                              <ClockIcon className="h-4 w-4" />
                              Active
                            </span>
                          )}
                        </div>
                        {project.ClientName && (
                          <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                            <BuildingOffice2Icon className="h-4 w-4" />
                            {project.ClientName}
                            {project.Industry && ` • ${project.Industry}`}
                          </p>
                        )}
                        <p className="text-sm text-gray-500 mt-2">{project.Description}</p>
                      </div>

                      {/* Profit Margin Gauge */}
                      <div className="ml-6 flex-shrink-0 w-32 text-center">
                        <div
                          className={`inline-flex items-center justify-center w-24 h-24 rounded-full border-8 ${
                            profitMargin < 20
                              ? 'border-red-500'
                              : profitMargin <= 30
                              ? 'border-yellow-500'
                              : 'border-emerald-500'
                          }`}
                        >
                          <div>
                            <div className="text-2xl font-bold text-gray-900">
                              {profitMargin}%
                            </div>
                            <div className="text-xs text-gray-500">Margin</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Project Info Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500">Start Date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDateSafe(project.StartDate) || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">End Date</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDateSafe(project.EndDate) || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Estimated Hours</p>
                        <p className="text-sm font-medium text-gray-900">
                          {project.EstimatedHours || 'N/A'} hrs
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Author</p>
                        <p className="text-sm font-medium text-gray-900">{project.Author}</p>
                      </div>
                    </div>

                    {/* Financial Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                      {/* Financial Breakdown */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h5 className="text-sm font-semibold text-gray-700 mb-3">
                          Financial Breakdown
                        </h5>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Project Price</span>
                            <span className="text-sm font-bold text-blue-600">
                              £{(project.Price || 0).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Labor Costs</span>
                            <span className="text-sm font-bold text-orange-600">
                              -£{(project.LaborCosts || 0).toLocaleString()}
                            </span>
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                            <span className="text-sm font-semibold text-gray-900">Net Profit</span>
                            <span
                              className={`text-sm font-bold ${
                                (project.NetProfit || 0) >= 0
                                  ? 'text-emerald-600'
                                  : 'text-red-600'
                              }`}
                            >
                              £{(project.NetProfit || 0).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Pie Chart */}
                      <div className="flex items-center justify-center">
                        <div style={{ height: 150, width: 150 }}>
                          <VictoryPie
                            data={[
                              { x: 'Profit', y: Math.max(0, project.NetProfit || 0) },
                              { x: 'Costs', y: project.LaborCosts || 0 },
                            ]}
                            colorScale={[getProfitMarginBgColor(profitMargin), '#f97316']}
                            labels={({ datum }) =>
                              `${datum.x}\n£${datum.y.toLocaleString()}`
                            }
                            labelComponent={
                              <VictoryLabel style={{ fontSize: 8, fill: 'white' }} />
                            }
                            innerRadius={40}
                            padding={20}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Materials Section */}
                    {materials.length > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center gap-2 mb-3">
                          <CubeIcon className="h-5 w-5 text-gray-600" />
                          <h5 className="text-sm font-semibold text-gray-700">
                            Materials ({materials.length} items)
                          </h5>
                          <span className="text-xs text-gray-500">
                            (Used: {usedMaterials.length} • Total Cost: £
                            {totalMaterialsCost.toLocaleString()})
                          </span>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {materials.slice(0, 6).map((material, idx) => (
                              <div
                                key={idx}
                                className="flex items-center justify-between text-xs bg-white rounded px-3 py-2"
                              >
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`w-2 h-2 rounded-full ${
                                      material.used
                                        ? 'bg-emerald-500'
                                        : material.bought
                                        ? 'bg-blue-500'
                                        : 'bg-gray-300'
                                    }`}
                                  ></span>
                                  <span className="font-medium text-gray-900">
                                    {material.name}
                                  </span>
                                  <span className="text-gray-500">
                                    ({material.category})
                                  </span>
                                </div>
                                <span className="text-gray-700 font-medium">
                                  £{(material.unit_cost || 0).toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>
                          {materials.length > 6 && (
                            <p className="text-xs text-gray-500 mt-2 text-center">
                              +{materials.length - 6} more materials
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Additional Info */}
                    {project.Objective && (
                      <div className="mt-4 bg-blue-50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-blue-900 mb-1">Objective</p>
                        <p className="text-sm text-blue-700">{project.Objective}</p>
                      </div>
                    )}

                    {project.Process && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500">
                          <span className="font-semibold">Processes:</span> {project.Process}
                        </p>
                      </div>
                    )}

                    {project.Notes && (
                      <div className="mt-2 bg-yellow-50 rounded-lg p-3">
                        <p className="text-xs font-semibold text-yellow-900 mb-1">Notes</p>
                        <p className="text-sm text-yellow-700">{project.Notes}</p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
